import { useFirestore, useFirestoreDocData, useUser } from "reactfire";
import { doc, setDoc } from "firebase/firestore";
import { FIRESTORE_CARTS_TABLE } from "../../../constants/FirebaseConstants";
import {
  consolidateAllProductLines,
  filterProduct,
  getPaymentAndDiscountPriceForGroup,
  isPreOrderProgramActive,
  splitQuantityOverStockAndPreOrder,
} from "../../../helpers/product";
import uuid from "uuid/v4";
import { db } from "../../../services/firebase";
import dayjs from "dayjs";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

export function useCart() {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const { data: user, status: userStatus } = useUser();
  const docRef = doc(
    firestore,
    FIRESTORE_CARTS_TABLE,
    userStatus === "loading" || !user ? "0" : user?.uid,
  );
  const { data, status } = useFirestoreDocData(docRef, { idField: "id" });
  const isLoading = status === "loading" || !user;

  const addProductToCartLegacy = async ({
    product,
    quantity,
    notification,
  }) => {
    if (user) {
      const currentCart = !!data ? data.products : [];
      const cartItem = filterProduct(currentCart, product);

      const newCart =
        cartItem === undefined
          ? [
              ...currentCart,
              {
                ...product,
                quantity: quantity ? quantity : 1,
                cartItemId: uuid(),
                addedAt: dayjs().unix(),
              },
            ]
          : currentCart.map((item) =>
              item.cartItemId === cartItem.cartItemId
                ? {
                    ...item,
                    quantity: quantity
                      ? item.quantity + quantity
                      : item.quantity + 1,
                  }
                : item,
            );

      try {
        await setDoc(
          doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
          { products: newCart, updatedAt: dayjs().unix() },
          { merge: true },
        );
        if (notification) {
          message.success(t("added_to_cart"));
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  // Centralized add with stock/pre-order split. Returns the actual split used.
  const addProductToCart = async ({ product, quantity, notification }) => {
    if (!user) return { normalQty: 0, preOrderQty: 0 };
    const rawCart = !!data ? data.products : [];

    const orderUnit = product.orderUnit || 1;
    const programActive = isPreOrderProgramActive(product.preOrder);

    // Heal any pre-existing duplicate normal/pre-order lines for this exact
    // product before adding more, so the new addition lands on a clean baseline
    const currentCart = consolidateAllProductLines(rawCart, (id) =>
      id === product.id
        ? { stock: product.stock, orderUnit, preOrder: product.preOrder }
        : null,
    );

    // If caller explicitly targets a pre-order line, add directly to pre-order
    // without consuming stock - but only while the program is actually running
    if (product.preOrderLine && programActive) {
      const requested = quantity || 1;
      let newCart = [...currentCart];
      const addLine = (lineProduct, qty) => {
        if (qty <= 0) return;
        const cartItem = filterProduct(newCart, lineProduct);
        if (cartItem === undefined) {
          newCart = [
            ...newCart,
            {
              ...lineProduct,
              quantity: qty,
              cartItemId: uuid(),
              addedAt: dayjs().unix(),
            },
          ];
        } else {
          newCart = newCart.map((item) =>
            item.cartItemId === cartItem.cartItemId
              ? { ...item, quantity: item.quantity + qty }
              : item,
          );
        }
      };

      addLine({ ...product, preOrderLine: true }, requested);
      try {
        await setDoc(
          doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
          { products: newCart, updatedAt: dayjs().unix() },
          { merge: true },
        );
        if (notification) message.success(t("added_to_cart"));
      } catch (err) {
        console.log(err);
      }
      return { normalQty: 0, preOrderQty: requested };
    }

    // Compute how many normal units are already in cart (exclude pre-order lines)
    const normalInCart = currentCart
      .filter((it) => it.id === product.id && !it.preOrderLine)
      .reduce((s, it) => s + (it.quantity || 0), 0);
    const stock = product.stock || 0;
    let inStockLeft = Math.max(0, stock - normalInCart);

    // Allocate from stock in multiples of orderUnit
    let allocatable = Math.floor(inStockLeft / orderUnit) * orderUnit;
    // Defensive: if no normal line exists and stock exists but allocatable 0, base on stock directly
    if (allocatable === 0 && stock >= orderUnit && normalInCart === 0) {
      allocatable = Math.floor(stock / orderUnit) * orderUnit;
    }

    const requested = quantity || 1;
    const normalQty = Math.min(requested, allocatable);
    // Surplus above stock is only orderable while the pre-order program runs;
    // once availableAt has passed, stock is all there is
    const preOrderQty = programActive ? Math.max(0, requested - normalQty) : 0;

    // Mutate a working copy in-memory, then write once
    let newCart = [...currentCart];

    const addLine = (lineProduct, qty) => {
      if (qty <= 0) return;
      const cartItem = filterProduct(newCart, lineProduct);
      if (cartItem === undefined) {
        newCart = [
          ...newCart,
          {
            ...lineProduct,
            quantity: qty,
            cartItemId: uuid(),
            addedAt: dayjs().unix(),
          },
        ];
      } else {
        newCart = newCart.map((item) =>
          item.cartItemId === cartItem.cartItemId
            ? { ...item, quantity: item.quantity + qty }
            : item,
        );
      }
    };

    // Add normal portion (ensure preOrderLine not set) - only if there's stock to allocate
    if (normalQty > 0) {
      addLine({ ...product, preOrderLine: false }, normalQty);
    }

    // Add pre-order portion if the program is active (already accounted for in preOrderQty)
    const preAllowed = preOrderQty > 0;
    if (preAllowed) {
      addLine({ ...product, preOrderLine: true }, preOrderQty);
    }

    try {
      await setDoc(
        doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
        { products: newCart, updatedAt: dayjs().unix() },
        { merge: true },
      );
      if (notification) message.success(t("added_to_cart"));
    } catch (err) {
      console.log(err);
    }

    return { normalQty, preOrderQty: preAllowed ? preOrderQty : 0 };
  };

  const addProductSetToCart = async ({ products }) => {
    const currentCart = data.products;
    let newCart = [...currentCart];
    products.forEach((product) => {
      const cartItem = filterProduct(currentCart, product);
      newCart =
        cartItem === undefined
          ? [
              ...newCart,
              {
                ...product,
                quantity: product.quantity ? product.quantity : 1,
                cartItemId: uuid(),
                addedAt: dayjs().unix(),
              },
            ]
          : newCart.map((item) =>
              item.cartItemId === cartItem.cartItemId
                ? {
                    ...item,
                    quantity: product.quantity
                      ? item.quantity + product.quantity
                      : item.quantity + 1,
                  }
                : item,
            );
    });

    try {
      await setDoc(
        doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
        {
          products: newCart,
          updatedAt: dayjs().unix(),
        },
        { merge: true },
      );
    } catch (err) {
      console.log(err);
    }
  };

  const addMultipleProductsToCart = async ({ products }) => {
    const currentCart = data.products;
    let cartItemsList = [...currentCart];
    products.forEach((product) => {
      const cartItem = filterProduct(currentCart, product);
      if (cartItem === undefined) {
        cartItemsList.push({
          ...product,
          quantity: product.quantity ? product.quantity : 1,
          cartItemId: uuid(),
          addedAt: dayjs().unix(),
        });
      } else {
        let existingCartItemProduct = cartItemsList.find(
          (item) => item.cartItemId === cartItem.cartItemId,
        );
        let index = cartItemsList.findIndex(
          (item) => item.cartItemId === cartItem.cartItemId,
        );
        cartItemsList[index].quantity = product.quantity
          ? existingCartItemProduct.quantity + product.quantity
          : existingCartItemProduct.quantity + 1;
      }
    });

    try {
      await setDoc(
        doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
        {
          products: cartItemsList,
          updatedAt: dayjs().unix(),
        },
        { merge: true },
      );
    } catch (err) {
      console.log(err);
    }
  };

  const decreaseProductQuantity = async ({ product }) => {
    const currentCart = data.products;
    const orderUnit = product.orderUnit || 1;
    if (product.quantity > orderUnit) {
      const newCart = currentCart.map((item) =>
        item.cartItemId === product.cartItemId
          ? { ...item, quantity: item.quantity - orderUnit }
          : item,
      );

      try {
        await setDoc(
          doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
          {
            products: newCart,
            updatedAt: dayjs().unix(),
          },
          { merge: true },
        );
      } catch (err) {
        console.log(err);
      }
    }
  };

  const deleteProductFromCart = async ({ product }) => {
    const currentCart = data.products;
    const remainingItems = (currentCart, product) =>
      currentCart.filter(
        (cartItem) => cartItem.cartItemId !== product.cartItemId,
      );

    try {
      await setDoc(doc(db, FIRESTORE_CARTS_TABLE, user?.uid), {
        products: remainingItems(currentCart, product),
        updatedAt: dayjs().unix(),
      });
    } catch (err) {
      console.log(err);
    }
  };

  const deleteAllProductsFromCart = async () => {
    try {
      if (user?.uid) {
        await setDoc(doc(db, FIRESTORE_CARTS_TABLE, user?.uid), {
          products: [],
          updatedAt: dayjs().unix(),
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateCart = async ({
    groupId,
    groupSettingsUserGroup,
    selectedSalesGroup,
    products,
  }) => {
    const currentCart = data?.products;
    if (
      !products ||
      products.length === 0 ||
      !currentCart ||
      currentCart.length === 0
    )
      return { removed: [], unavailable: [] };
    else {
      const removed = [];
      const unavailable = [];
      // Units of stock already taken by earlier lines of the same product, so
      // two lines never allocate the same units twice
      const stockClaimedByProduct = new Map();
      const newCart = currentCart.reduce(function (updatedCart, cartItem) {
        const productFound = products.find(
          (product) => product.id === cartItem.id,
        );
        const productPriceForUser = getPaymentAndDiscountPriceForGroup(
          productFound,
          groupId,
          groupSettingsUserGroup,
          selectedSalesGroup,
        );

        if (!productFound) {
          removed.push({
            id: cartItem.id,
            name: cartItem.name,
            quantity: cartItem.quantity,
          });
          return updatedCart;
        }

        // Re-evaluate against the CMS as it is now, not as it was when the
        // line was added: stock first, the surplus only while the pre-order
        // program is still running. What is left over is not deliverable, but
        // stays in the cart as such - see the note in consolidateProductLines
        const stockClaimed = stockClaimedByProduct.get(cartItem.id) || 0;
        const { normalQty, preOrderQty, unavailableQty } =
          splitQuantityOverStockAndPreOrder(cartItem.quantity, {
            stock: productFound.stock,
            orderUnit: productFound.orderUnit,
            preOrder: productFound.preOrder,
            stockAlreadyClaimed: stockClaimed,
          });
        stockClaimedByProduct.set(cartItem.id, stockClaimed + normalQty);

        if (unavailableQty > 0) {
          unavailable.push({
            id: cartItem.id,
            name: productFound.name,
            quantity: unavailableQty,
          });
        }

        updatedCart.push({
          ...cartItem,
          name: productFound.name,
          image: productFound.image,
          price: productPriceForUser.original,
          discount: productFound.discount,
          taxRate: productFound.taxRate,
          stock: productFound.stock,
          // The CMS is the source of truth for the pre-order window; a snapshot
          // taken at add-to-cart time goes stale the moment the date is moved
          preOrder: productFound.preOrder || null,
          // Quantity stays whole; consolidateAllProductLines below cuts the
          // line into its stock / pre-order / not-deliverable parts
          preOrderLine: normalQty === 0 && preOrderQty > 0,
          unavailableLine: normalQty === 0 && preOrderQty === 0,
        });
        return updatedCart;
      }, []);

      // Heal duplicate lines for the same product/variant and rebuild the split
      // over stock / pre-order / not-deliverable from the current CMS state
      const consolidatedCart = consolidateAllProductLines(newCart, (id) => {
        const productFound = products.find((product) => product.id === id);
        return productFound
          ? {
              stock: productFound.stock,
              orderUnit: productFound.orderUnit,
              preOrder: productFound.preOrder,
            }
          : null;
      });

      if (!isEqual(consolidatedCart, currentCart)) {
        try {
          await setDoc(
            doc(db, FIRESTORE_CARTS_TABLE, user?.uid),
            {
              products: consolidatedCart,
              updatedAt: dayjs().unix(),
            },
            { merge: true },
          );
        } catch (err) {
          console.log(err);
        }
      }

      return { removed, unavailable };
    }
  };

  return {
    data,
    isLoading,
    addProductToCartLegacy,
    addProductToCart,
    addProductSetToCart,
    addMultipleProductsToCart,
    decreaseProductQuantity,
    deleteProductFromCart,
    deleteAllProductsFromCart,
    updateCart,
  };
}
